import { SelectionModel } from "@angular/cdk/collections";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTableExporterDirective } from "mat-table-exporter";
import { MatRow } from "@angular/material/table";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Subscription } from "rxjs";
import { Model } from "src/app/models/model";
import { PaginationInfo } from "src/app/models/paginationInfo";
import { User } from "src/app/models/user";
import { AuthService } from "src/app/services/auth.service";
import { ResourcefulService } from "src/app/services/resourcefulService";
import { dataTableColumnDefinition, HoverPreviewConfig } from "../dataTableColumnDefinition";
import { PaginatorComponent } from "../paginator/paginator.component";
import { QuadroRiassuntivoService } from "src/app/services/statistiche/quadro-riassuntivo.service";
import { ActivatedRoute } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

@Component({
  selector: "resourceful-index",
  templateUrl: "./resourceful-index.component.html",
  styleUrls: ["./resourceful-index.component.scss"],
})
export class ResourcefulIndexComponent implements OnInit, AfterViewInit, OnDestroy {
  selection = new SelectionModel<any>(true, []);
  @ViewChild("tableContainer") tableContainer: ElementRef<HTMLDivElement>;
  @ViewChildren(MatRow, { read: ElementRef })
  rowElements: QueryList<ElementRef<HTMLTableRowElement>>;

  dragSelection = {
    active: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  };

  private initialSelectionDuringDrag = new Set<any>();
  private dragWithModifier = false;
  private dragMoved = false;
  private boundMouseMove: (event: MouseEvent) => void;
  private boundMouseUp: (event: MouseEvent) => void;
  private rowChangesSubscription?: Subscription;
  hoverPreviewState: {
    columnName: string;
    model: any;
    config: HoverPreviewConfig;
    trustedUrl: SafeResourceUrl | null;
    style: { top: number; left: number };
  } | null = null;
  private inlineEditStates = new Map<string, InlineEditState>();
  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection?.selected?.length;
    const numRows = this.models?.length || 0;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.models?.forEach((row) => this.selection.select(row));
  }

  @Input() pagination = true;

  @Input() columnDefinitions: dataTableColumnDefinition[] = [
    new dataTableColumnDefinition({
      humanName: "ID",
      internalName: "id",
    }),
    new dataTableColumnDefinition({
      humanName: "Nome",
      internalName: "nome",
    }),
  ];

  @Input() selectionMode: boolean = false;

  @Input() showReset: boolean = false;

  @Input() multipleSelection: boolean = false;

  @Output() selectedModel: EventEmitter<Model> = new EventEmitter();
  form: FormGroup; //form that keeps track of which columns are hidden and which not

  get namesOfColumnsThatShouldBeDisplayed() {
    let columnNames = [];

    //Warning: non manipolare le colonne da visualizzare prima di queste righe che le filtrano in base alla checkbox nell'header della colonna
    //ad esempio se aggiungo una colonna qui tipo this.columnDefinitions.push("colonnaACaso") dopo verrà tolta dal filter.

    columnNames = columnNames.concat(
      //add all internal names of column definitions
      this.columnDefinitions.map(
        (columnDefinition) => columnDefinition.internalName
      )
    );

    if (this.allowHidingColumns && this.form) {
      //if it's allowed to hide columns (default: true) then filter out the columns that aren't checked in the column selector.
      columnNames = columnNames.filter((v) => this.isChecked(v));
    }
    //Sezione di aggiunta colonne speciali (selezioni, azioni varie)
    if (this.multipleSelection)
      //if multiple selection required...
      columnNames.unshift("select");

    //if there's at least 1 action push actions col
    if (this.deleteFunction || this.editFunction || this.downloadFunction || this.previewFunction)
      columnNames.push("actions");

    if (this.selectionMode) {
      //if single selection required, put single selection col at beginning and end of table columns
      columnNames.push("selectThisRow");
      columnNames.unshift("selectThisRow");
    }
    return columnNames;
  }

  @Output() addClicked: EventEmitter<void> = new EventEmitter();

  toggleVal(column) {
    this.form?.controls[column.internalName]?.setValue(
      !this.form?.controls[column.internalName]?.value
    );
  }

  isChecked(name: string): boolean {
    return !!this.form?.controls[name]?.value;
  }

  @Input() deleteFunction: (model) => any;
  @Input() editFunction: (model) => any;
  @Input() downloadFunction: (model) => any;
  @Input() previewFunction: (model) => any;
  @Input() rowClassGetter?: (model: any) => string | string[] | Set<string> | { [klass: string]: any };

  @Input() showAddButton: boolean = false;
  @Input() addButtonText: string = "Aggiungi";
  @Input() addButtonIcon: string = "add";
  @Input() addFunction: () => void;

  @Input() canDeleteModels: (user: User) => boolean = () => true;

  @Input() relations: string[] = [];
  @Input() modelService: ResourcefulService<any>;
  @Input() overrideResults: boolean;
  @Input() results: any;

  @Input() overrideFunction: (search, paginationInfo) => any;

  @Input() set search(newSearch) {
    console.log(newSearch);
    this._search = this.removeNullsFromSearch(newSearch);

    this.customPaginator?.searchChanged();
  }
  _search: any;

  //custom paginator
  @ViewChild("customPaginator") public customPaginator: PaginatorComponent;

  models: Model[];

  loading: boolean = true;

  loggedInUser: User;

  currentSort = new BehaviorSubject<MatSort>({} as MatSort);
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private toast: ToastrService,
    public dialog: MatDialog,
    private authService: AuthService,
    private quadroRiassuntivoService: QuadroRiassuntivoService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {
    this.boundMouseMove = this.onWindowMouseMove.bind(this);
    this.boundMouseUp = this.onWindowMouseUp.bind(this);
  }

  async ngOnInit() {
    this.form = new FormGroup({});
    for (let col of this.columnDefinitions) {
      this.form.addControl(col.internalName, new FormControl([true]));
    }

    this.loggedInUser = await this.authService.loggedInUser.getValue();

    await this.updateData();
  }

  ngAfterViewInit(): void {
    this.rowChangesSubscription = this.rowElements?.changes.subscribe(() => {
      if (!this.dragSelection.active) {
        return;
      }
      this.cancelDragSelection();
    });
  }

  ngOnDestroy(): void {
    this.rowChangesSubscription?.unsubscribe();
    this.detachDragListeners();
  }

  @Output() newModelsLoaded: EventEmitter<void> = new EventEmitter();

  public async updateData() {
    let paginationInfo: PaginationInfo = {
      sortDirection: this.currentSort?.getValue().direction,
      sortedColumn: this.currentSort?.getValue().active,
      //make page index start by 0 for easier usage in backend (subtract 1)
      requestedPage: this.customPaginator?.currentPage - 1,
      objectsPerPage: this.customPaginator?.objectsPerPage,
      search: this._search,
      desiresPagination: true,
    };
    console.log(this._search);

    try {
      this.loading = true;
      if (!this.overrideResults)
        this.models = await this.modelService.getAll(
          this.relations,
          paginationInfo
        );
      else
        this.models = await this.overrideFunction(this._search, paginationInfo);

      this.customPaginator?.updatePaginationNumbers(this.modelService.count);
      this.newModelsLoaded.emit();
      console.log(this.models);
    } catch (e) {
      console.error(e);
      this.toast.error("Impossibile caricare i modelli", "Errore!");
    }
    this.loading = false;
    this.cleanInlineEditStates();
  }

  async applySort(sort: MatSort) {
    this.currentSort.next(sort);
    await this.updateData();
  }

  public onAddClick() {
    if (this.addFunction) {
      this.addFunction();
    }
    this.addClicked.emit();
  }

  public selectedRow(model) {
    this.selectedModel.emit(model);
  }

  onBooleanClick(event: MouseEvent, column: dataTableColumnDefinition, model: any) {
    event.stopPropagation();
    column.onClick?.(model);
  }

  onTableMouseDown(event: MouseEvent) {
    if (!this.multipleSelection) {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    const container = this.tableContainer?.nativeElement;
    if (!container) {
      return;
    }

    const targetElement = event.target as HTMLElement | null;
    if (!targetElement || !targetElement.closest("table")) {
      return;
    }

    if (this.isInteractiveElement(targetElement)) {
      return;
    }

    const point = this.getRelativePoint(event, container);
    this.dragSelection.active = true;
    this.dragSelection.startX = point.x;
    this.dragSelection.startY = point.y;
    this.dragSelection.currentX = point.x;
    this.dragSelection.currentY = point.y;
    this.dragMoved = false;
    this.dragWithModifier = event.ctrlKey || event.metaKey;
    this.initialSelectionDuringDrag = new Set<any>(this.selection.selected);

    if (!this.dragWithModifier) {
      this.selection.clear();
    }

    this.attachDragListeners();
    event.preventDefault();
  }

  private onWindowMouseMove(event: MouseEvent) {
    if (!this.dragSelection.active) {
      return;
    }

    const container = this.tableContainer?.nativeElement;
    if (!container) {
      return;
    }

    const point = this.getRelativePoint(event, container);
    this.dragSelection.currentX = point.x;
    this.dragSelection.currentY = point.y;
    this.dragMoved = true;

    this.updateDragSelection();
    event.preventDefault();
  }

  private onWindowMouseUp(event: MouseEvent) {
    if (!this.dragSelection.active) {
      return;
    }

    if (!this.dragMoved) {
      this.handleSimpleClickSelection(event);
    }

    this.cancelDragSelection();
    event.preventDefault();
  }

  private handleSimpleClickSelection(event: MouseEvent) {
    const targetElement = event.target as HTMLElement | null;
    if (!targetElement) {
      return;
    }
    const rowElement = targetElement.closest("tr.mat-row");
    if (!rowElement) {
      return;
    }
    const rowData = this.getRowDataFromElement(rowElement as HTMLTableRowElement);
    if (!rowData) {
      return;
    }

    if (!this.dragWithModifier) {
      this.selection.clear();
      this.selection.select(rowData);
      return;
    }

    if (this.selection.isSelected(rowData)) {
      this.selection.deselect(rowData);
    } else {
      this.selection.select(rowData);
    }
  }

  private updateDragSelection() {
    if (!this.dragSelection.active) {
      return;
    }

    const container = this.tableContainer?.nativeElement;
    if (!container) {
      return;
    }

    const rect = this.getCurrentSelectionRect();
    if (!rect) {
      return;
    }

    const rows = this.rowElements?.toArray() ?? [];
    const selectionSet = new Set<any>(
      this.dragWithModifier ? this.initialSelectionDuringDrag : []
    );

    rows.forEach((rowRef, index) => {
      const rowRect = this.getRowRectRelativeToContainer(
        rowRef.nativeElement,
        container
      );
      if (this.rectanglesIntersect(rect, rowRect)) {
        const data = this.models?.[index];
        if (data) {
          selectionSet.add(data);
        }
      }
    });

    this.selection.clear();
    selectionSet.forEach((model) => this.selection.select(model));
  }

  private getCurrentSelectionRect() {
    const { startX, startY, currentX, currentY } = this.dragSelection;
    const left = Math.min(startX, currentX);
    const right = Math.max(startX, currentX);
    const top = Math.min(startY, currentY);
    const bottom = Math.max(startY, currentY);

    const width = right - left;
    const height = bottom - top;

    if (width === 0 && height === 0) {
      return null;
    }

    return { left, top, right, bottom, width, height };
  }

  get selectionBoxStyles(): { [key: string]: string } {
    if (!this.dragSelection.active) {
      return {};
    }

    const rect = this.getCurrentSelectionRect();
    if (!rect) {
      return { display: "none" };
    }

    return {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${Math.max(rect.width, 0)}px`,
      height: `${Math.max(rect.height, 0)}px`,
    };
  }

  private getRelativePoint(event: MouseEvent, container: HTMLElement) {
    const rect = container.getBoundingClientRect();
    return {
      x: event.clientX - rect.left + container.scrollLeft,
      y: event.clientY - rect.top + container.scrollTop,
    };
  }

  private getRowRectRelativeToContainer(row: HTMLElement, container: HTMLElement) {
    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();

    const left = rowRect.left - containerRect.left + container.scrollLeft;
    const top = rowRect.top - containerRect.top + container.scrollTop;
    const right = left + rowRect.width;
    const bottom = top + rowRect.height;

    return { left, top, right, bottom };
  }

  private rectanglesIntersect(
    a: { left: number; right: number; top: number; bottom: number },
    b: { left: number; right: number; top: number; bottom: number }
  ) {
    return (
      a.left < b.right &&
      a.right > b.left &&
      a.top < b.bottom &&
      a.bottom > b.top
    );
  }

  onPreviewEnter(event: MouseEvent, model: any, column: dataTableColumnDefinition): void {
    if (!column.hoverPreview) {
      return;
    }
    const config = column.hoverPreview(model);
    if (!config) {
      if (
        this.hoverPreviewState &&
        this.hoverPreviewState.model === model &&
        this.hoverPreviewState.columnName === column.internalName
      ) {
        this.hoverPreviewState = null;
      }
      return;
    }
    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      return;
    }
    const style = this.computePreviewStyle(target);
    const trustedUrl =
      config.kind === "pdf"
        ? this.sanitizer.bypassSecurityTrustResourceUrl(
            this.appendPdfViewerFragment(config.url)
          )
        : null;
    this.hoverPreviewState = {
      columnName: column.internalName,
      model,
      config,
      trustedUrl,
      style,
    };
  }

  onPreviewMove(event: MouseEvent, model: any, column: dataTableColumnDefinition): void {
    if (
      !this.hoverPreviewState ||
      this.hoverPreviewState.model !== model ||
      this.hoverPreviewState.columnName !== column.internalName
    ) {
      return;
    }
    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      return;
    }
    this.hoverPreviewState = {
      ...this.hoverPreviewState,
      style: this.computePreviewStyle(target),
    };
  }

  onPreviewLeave(model: any, column: dataTableColumnDefinition): void {
    if (
      this.hoverPreviewState &&
      this.hoverPreviewState.model === model &&
      this.hoverPreviewState.columnName === column.internalName
    ) {
      this.hoverPreviewState = null;
    }
  }

  isPreviewVisible(model: any, column: dataTableColumnDefinition): boolean {
    return (
      !!this.hoverPreviewState &&
      this.hoverPreviewState.model === model &&
      this.hoverPreviewState.columnName === column.internalName
    );
  }

  getPreviewUrl(model: any, column: dataTableColumnDefinition): string | null {
    if (!this.isPreviewVisible(model, column)) {
      return null;
    }
    const config = this.hoverPreviewState?.config;
    if (!config || config.kind !== "image") {
      return null;
    }
    return config.url;
  }

  getPreviewStyles(model: any, column: dataTableColumnDefinition): { [key: string]: string } {
    if (!this.isPreviewVisible(model, column)) {
      return {};
    }
    const style = this.hoverPreviewState?.style;
    if (!style) {
      return {};
    }
    return {
      top: `${style.top}px`,
      left: `${style.left}px`,
    };
  }

  getPreviewKind(model: any, column: dataTableColumnDefinition): "image" | "pdf" | null {
    if (!this.isPreviewVisible(model, column)) {
      return null;
    }
    return this.hoverPreviewState?.config?.kind ?? null;
  }

  getPdfPreviewUrl(model: any, column: dataTableColumnDefinition): SafeResourceUrl | null {
    if (!this.isPreviewVisible(model, column)) {
      return null;
    }
    return this.hoverPreviewState?.trustedUrl ?? null;
  }

  private computePreviewStyle(target: HTMLElement): { top: number; left: number } {
    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const margin = 16;
    const maxWidth = Math.min(600, viewportWidth * 0.9);
    const maxHeight = Math.min(640, viewportHeight * 0.9);
    const halfWidth = maxWidth / 2;
    const halfHeight = maxHeight / 2;

    const spaceRight = viewportWidth - rect.right - margin;
    const spaceLeft = rect.left - margin;
    const elementCenterX = rect.left + rect.width / 2;
    const elementCenterY = rect.top + rect.height / 2;

    let centerX: number;
    let top: number;

    if (spaceRight >= maxWidth) {
      centerX = rect.right + margin + halfWidth;
      const boundedCenterY = Math.min(
        Math.max(elementCenterY, margin + halfHeight),
        viewportHeight - margin - halfHeight
      );
      top = boundedCenterY - halfHeight;
    } else if (spaceLeft >= maxWidth) {
      centerX = rect.left - margin - halfWidth;
      const boundedCenterY = Math.min(
        Math.max(elementCenterY, margin + halfHeight),
        viewportHeight - margin - halfHeight
      );
      top = boundedCenterY - halfHeight;
    } else {
      centerX = Math.min(
        Math.max(elementCenterX, halfWidth + margin),
        viewportWidth - halfWidth - margin
      );
      top = rect.bottom + margin;
      if (top + maxHeight > viewportHeight - margin) {
        top = Math.max(margin, rect.top - margin - maxHeight);
      }
    }

    return { top, left: centerX };
  }

  private appendPdfViewerFragment(url: string): string {
    if (!url) {
      return url;
    }
    if (url.includes("#")) {
      return url;
    }
    return `${url}#toolbar=0&navpanes=0&scrollbar=0`;
  }

  private getRowDataFromElement(rowElement: HTMLTableRowElement) {
    const rowEls = this.rowElements?.toArray() ?? [];
    const index = rowEls.findIndex((ref) => ref.nativeElement === rowElement);
    if (index === -1) {
      return null;
    }
    return this.models?.[index] ?? null;
  }

  private attachDragListeners() {
    this.detachDragListeners();
    document.addEventListener("mousemove", this.boundMouseMove);
    document.addEventListener("mouseup", this.boundMouseUp);
  }

  private detachDragListeners() {
    document.removeEventListener("mousemove", this.boundMouseMove);
    document.removeEventListener("mouseup", this.boundMouseUp);
  }

  private cancelDragSelection() {
    this.dragSelection.active = false;
    this.dragSelection.startX = 0;
    this.dragSelection.startY = 0;
    this.dragSelection.currentX = 0;
    this.dragSelection.currentY = 0;
    this.initialSelectionDuringDrag.clear();
    this.dragMoved = false;
    this.dragWithModifier = false;
    this.detachDragListeners();
  }

  private isInteractiveElement(element: HTMLElement): boolean {
    const interactiveSelector =
      "button, a, input, textarea, select, mat-checkbox, [mat-icon-button], [role=\"button\"], [role=\"checkbox\"], [contenteditable=\"true\"], .no-drag-select";
    return !!element.closest(interactiveSelector);
  }

  removeNullsFromSearch(originalSearch: any): any {
    let newSearch = {};

    for (const key in originalSearch) {
      if (originalSearch[key] !== null) {
        newSearch[key] = originalSearch[key];
      }
    }
    return newSearch;
  }

  @Input() allowHidingColumns = true;
  @Input() notShowDelete = true;

  @ViewChild(MatTableExporterDirective) exporter: MatTableExporterDirective;

  private getModelIdentifier(model: any): string {
    const modelId = (model as any)?.id;
    if (modelId === undefined || modelId === null) {
      const idx = this.models?.indexOf(model);
      return `idx-${idx ?? "x"}`;
    }
    return `id-${modelId}`;
  }

  getInlineKey(model: any, column: dataTableColumnDefinition): string {
    return `${column.internalName}::${this.getModelIdentifier(model)}`;
  }

  isInlineEditing(model: any, column: dataTableColumnDefinition): boolean {
    return !!this.inlineEditStates.get(this.getInlineKey(model, column))?.editing;
  }

  isInlineSaving(model: any, column: dataTableColumnDefinition): boolean {
    return !!this.inlineEditStates.get(this.getInlineKey(model, column))?.saving;
  }

  getInlineValue(model: any, column: dataTableColumnDefinition): string {
    const state = this.inlineEditStates.get(this.getInlineKey(model, column));
    if (state) {
      return state.value;
    }
    const currentVal = column.displayFunction(model);
    if (currentVal === null || currentVal === undefined) {
      return "";
    }
    return String(currentVal);
  }

  getInlineError(model: any, column: dataTableColumnDefinition): string | null {
    const state = this.inlineEditStates.get(this.getInlineKey(model, column));
    return state?.error ?? null;
  }

  startInlineEdit(model: any, column: dataTableColumnDefinition): void {
    if (!column.inlineEdit) {
      return;
    }
    const key = this.getInlineKey(model, column);
    const currentValRaw = column.displayFunction(model);
    const currentVal = currentValRaw === null || currentValRaw === undefined ? "" : String(currentValRaw);
    this.inlineEditStates.set(key, {
      value: currentVal,
      initialValue: currentVal,
      saving: false,
      editing: true,
      error: undefined,
    });
    requestAnimationFrame(() => {
      const selector = `[data-inline-key="${key}"]`;
      const input = this.tableContainer?.nativeElement.querySelector(
        selector
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  onInlineInput(value: string, model: any, column: dataTableColumnDefinition): void {
    const key = this.getInlineKey(model, column);
    const state = this.inlineEditStates.get(key);
    if (!state) {
      return;
    }
    this.inlineEditStates.set(key, {
      ...state,
      value,
      error: undefined,
    });
  }

  onInlineEnter(event: KeyboardEvent, model: any, column: dataTableColumnDefinition): void {
    event.preventDefault();
    this.commitInlineEdit(model, column);
  }

  onInlineEscape(event: KeyboardEvent, model: any, column: dataTableColumnDefinition): void {
    event.preventDefault();
    const key = this.getInlineKey(model, column);
    this.inlineEditStates.delete(key);
  }

  onInlineBlur(model: any, column: dataTableColumnDefinition): void {
    this.commitInlineEdit(model, column);
  }

  private async commitInlineEdit(model: any, column: dataTableColumnDefinition): Promise<void> {
    const config = column.inlineEdit;
    if (!config) {
      return;
    }
    const key = this.getInlineKey(model, column);
    const state = this.inlineEditStates.get(key);
    if (!state || state.saving) {
      return;
    }

    const rawValue = state.value ?? "";
    const trimmedValue =
      config.trim === false ? rawValue : rawValue.trim();
    const initialValue =
      config.trim === false ? state.initialValue ?? "" : (state.initialValue ?? "").trim();

    if (trimmedValue === initialValue) {
      this.inlineEditStates.delete(key);
      return;
    }

    const validationError = config.validate?.(trimmedValue, model);
    if (validationError) {
      this.inlineEditStates.set(key, {
        ...state,
        error: validationError,
      });
      return;
    }

    this.inlineEditStates.set(key, { ...state, saving: true, error: undefined });

    try {
      await config.save(model, trimmedValue);
      this.inlineEditStates.delete(key);
    } catch (e) {
      console.error(e);
      this.inlineEditStates.set(key, {
        ...state,
        saving: false,
        error: "Salvataggio non riuscito",
      });
    }
  }

  private cleanInlineEditStates(): void {
    if (!this.models?.length) {
      this.inlineEditStates.clear();
      return;
    }
    const validIds = new Set(this.models.map((m) => this.getModelIdentifier(m)));
    for (const key of Array.from(this.inlineEditStates.keys())) {
      const [, idPart] = key.split("::");
      if (idPart && !validIds.has(idPart)) {
        this.inlineEditStates.delete(key);
      }
    }
  }
}

type InlineEditState = {
  value: string;
  initialValue: string;
  saving: boolean;
  editing: boolean;
  error?: string;
};
